import { SearchForAddressByZipCodeContent } from 'fetcher';
import { FormikProps } from 'formik';
import {
  AssortedDrillDownPropertiesGroup,
  convertValuesForDrillDownFromAssortedPropertiesGroup,
} from 'models';
import {
  FormPage,
  FormPageInputData,
  FormPageInputItem,
  FormPageInputItemSingleSelectionPulldown,
  FormPageInputItemTextSingleLine,
  FormPageProperties,
  FormPageProperty,
  INPUT_ITEM_TYPE,
  PROPERTY_TYPE,
  SingleSelectionPropertyPulldown,
  SINGLE_SELECTION_PROPERTY_INPUT_TYPE,
  TextPropertyZipCodeWithoutHyphen,
  valueOf,
  FormPageInputItemTextZipCode,
  FormPageInputItemTextZipCodeWithoutHyphen,
} from 'shared';

export type AutoCompleteAddressPayload = {
  zipCodePropertyId: string;
  addressData: SearchForAddressByZipCodeContent;
  formikProps: FormikProps<FormPageInputData>;
  searchSetting: Required<
    TextPropertyZipCodeWithoutHyphen['inputFormat']
  >['searchSetting'];
  formPage: FormPage | null;
  assortedDrillDownPropertiesGroup?: AssortedDrillDownPropertiesGroup;
};

const getZipCodeInputItem = (
  values: FormPageInputData,
  zipCode: string,
  zipCodePropertyId: string,
):
  | FormPageInputItemTextZipCode
  | FormPageInputItemTextZipCodeWithoutHyphen
  | null => {
  const inputItem = values[zipCodePropertyId];
  if (inputItem?.inputItemType === INPUT_ITEM_TYPE.textZipCode) {
    return {
      ...inputItem,
      first: zipCode.slice(0, 3),
      second: zipCode.slice(3),
    };
  }
  if (inputItem?.inputItemType === INPUT_ITEM_TYPE.textZipCodeWithoutHyphen) {
    return {
      ...inputItem,
      zipCode,
    };
  }
  return null;
};

const getAddressTextSingleLineInputItem = (
  values: FormPageInputData,
  address: string,
  addressId: string,
): FormPageInputItemTextSingleLine | null => {
  const inputItem = values[addressId];
  if (inputItem?.inputItemType === INPUT_ITEM_TYPE.textSingleLine) {
    return {
      ...inputItem,
      text: address,
    };
  }
  return null;
};

const getPrefectureInputItemTextSingleLine = (
  inputItem: FormPageInputItemTextSingleLine,
  address: string,
): FormPageInputItemTextSingleLine | null => {
  return {
    ...inputItem,
    text: address,
  };
};

const isPropertySingleSelectionPulldown = (
  property: FormPageProperty | undefined,
): property is SingleSelectionPropertyPulldown => {
  if (property?.propertyType !== PROPERTY_TYPE.singleSelection) {
    return false;
  }
  return (
    property.inputFormat.inputType ===
    SINGLE_SELECTION_PROPERTY_INPUT_TYPE.pulldown
  );
};

const getPrefectureInputItemSingleSelectionPulldown = (
  inputItem: FormPageInputItemSingleSelectionPulldown,
  address: string,
  property: FormPageProperty,
): FormPageInputItemSingleSelectionPulldown | null => {
  if (!isPropertySingleSelectionPulldown(property)) {
    return null;
  }
  const { candidates } = property;
  const value = candidates.find(
    (candidate) => candidate.label === address,
  )?.value;
  if (value == null) {
    return null;
  }
  return {
    ...inputItem,
    value,
  };
};

type GetPrefectureInputItemPayload = {
  values: FormPageInputData;
  addressId: string;
  properties: FormPageProperties;
  address: {
    textSingleLine: string;
    singleSelectionPulldown: string;
  };
};
const getPrefectureInputItem = ({
  values,
  addressId,
  properties,
  address,
}: GetPrefectureInputItemPayload):
  | FormPageInputItemSingleSelectionPulldown
  | FormPageInputItemTextSingleLine
  | null => {
  const inputItem = values[addressId];
  if (!inputItem) {
    return null;
  }
  switch (inputItem.inputItemType) {
    case INPUT_ITEM_TYPE.textSingleLine:
      return getPrefectureInputItemTextSingleLine(
        inputItem,
        address.textSingleLine,
      );
    case INPUT_ITEM_TYPE.singleSelectionPulldown: {
      const property = properties[addressId];
      return getPrefectureInputItemSingleSelectionPulldown(
        inputItem,
        address.singleSelectionPulldown,
        property,
      );
    }
    default:
      return null;
  }
};

type GetUpdateValuesPayload = Pick<
  AutoCompleteAddressPayload,
  'zipCodePropertyId' | 'addressData' | 'searchSetting'
> & {
  values: FormPageInputData;
  properties: FormPageProperties;
};

type UpdatedValues = {
  [propertyId: string]: FormPageInputItem | null;
};

export const ADDRESS_UPDATE_PATTERN = {
  prefectureOnly: 'prefectureOnly',
  cityAndTownCombined: 'cityAndTownCombined',
  prefectureAndCityConbined: 'prefectureAndCityConbined',
  prefectureAndTownCombined: 'prefectureAndTownCombined',
  allSeparate: 'allSeparate',
  doNothing: 'doNothing',
} as const;

type AddressUpdatePattern = valueOf<typeof ADDRESS_UPDATE_PATTERN>;

export const getAddressUpdatePattern = (
  prefecture: string,
  city: string | undefined,
  town: string | undefined,
): AddressUpdatePattern => {
  // 設定上ないが何も入ってない場合
  if (!prefecture && !city && !town) {
    return ADDRESS_UPDATE_PATTERN.doNothing;
  }
  // 都道府県項目のみ設定されている
  if (!city && !town) {
    return ADDRESS_UPDATE_PATTERN.prefectureOnly;
  }
  // 町村項目が未設定
  if (city && !town) {
    if (prefecture === city) {
      // 都道府県項目、市区郡項目が同一項目
      return ADDRESS_UPDATE_PATTERN.prefectureOnly;
    }
    // 都道府県項目、市区郡項目が別項目で、町村項目が未設定
    return ADDRESS_UPDATE_PATTERN.cityAndTownCombined;
  }

  // 全項目設定済
  if (city && town) {
    if (prefecture === city && city === town) {
      // 都道府県項目、市区郡項目、町村項目が全て同じ項目
      return ADDRESS_UPDATE_PATTERN.prefectureOnly;
    }
    if (prefecture === city && city !== town) {
      // 都道府県項目、市区郡項目が同じ項目で、町村項目が別項目で設定
      return ADDRESS_UPDATE_PATTERN.prefectureAndCityConbined;
    }
    if (prefecture !== city && city === town) {
      // 都道府県項目、市区郡項目が別項目、市区郡項目と町村項目が同じ項目
      return ADDRESS_UPDATE_PATTERN.cityAndTownCombined;
    }
    if (prefecture === town && city !== town) {
      // 都道府県項目、町村項目が同じ項目、市区郡項目のみ別項目
      return ADDRESS_UPDATE_PATTERN.prefectureAndTownCombined;
    }
    // 都道府県項目、市区郡項目、町村項目が別項目
    return ADDRESS_UPDATE_PATTERN.allSeparate;
  }
  return ADDRESS_UPDATE_PATTERN.doNothing; // 設定上あり得ないパターン
};

const getUpdatedValues = ({
  zipCodePropertyId,
  addressData,
  values,
  searchSetting,
  properties,
}: GetUpdateValuesPayload): UpdatedValues => {
  const {
    prefecture: prefectureId,
    city: cityId,
    town: townId,
  } = searchSetting;
  const { zipcode, prefecture, city, town } = addressData;
  // デジタルアドレスで検索された場合を考慮して、APIの戻り値に含まれる郵便番号で上書きできるようにしている
  // see: https://gitlab.dev-internal.crmstyle.com/synergy/newt/form/portal/-/issues/4840
  const zipCodeInputValue = getZipCodeInputItem(
    values,
    zipcode,
    zipCodePropertyId,
  );
  const updatePattern = getAddressUpdatePattern(prefectureId, cityId, townId);
  switch (updatePattern) {
    case ADDRESS_UPDATE_PATTERN.prefectureOnly:
      // 都道府県項目のみにセットするパターン(プルダウン型なら都道府県のみ、文字列型なら全部結合)
      return {
        [zipCodePropertyId]: zipCodeInputValue,
        [prefectureId]: getPrefectureInputItem({
          values,
          addressId: prefectureId,
          properties,
          address: {
            textSingleLine: `${prefecture}${city}${town}`,
            singleSelectionPulldown: prefecture,
          },
        }),
      };
    case ADDRESS_UPDATE_PATTERN.cityAndTownCombined:
      // 都道府県項目に都道府県をセット、市区郡項目に市区郡と町村を結合してセットするパターン
      if (cityId == null) return {};
      return {
        [zipCodePropertyId]: zipCodeInputValue,
        [prefectureId]: getPrefectureInputItem({
          values,
          addressId: prefectureId,
          properties,
          address: {
            textSingleLine: prefecture,
            singleSelectionPulldown: prefecture,
          },
        }),
        [cityId]: getAddressTextSingleLineInputItem(
          values,
          `${city}${town}`,
          cityId,
        ),
      };
    case ADDRESS_UPDATE_PATTERN.prefectureAndCityConbined:
      // 都道府県項目に都道府県項目と市区郡の値を結合、町村項目に町村の値をセットするパターン
      if (townId == null) return {};
      return {
        [zipCodePropertyId]: zipCodeInputValue,
        [prefectureId]: getPrefectureInputItem({
          values,
          addressId: prefectureId,
          properties,
          address: {
            textSingleLine: `${prefecture}${city}`,
            singleSelectionPulldown: prefecture,
          },
        }),
        [townId]: getAddressTextSingleLineInputItem(values, town, townId),
      };
    case ADDRESS_UPDATE_PATTERN.prefectureAndTownCombined:
      // 都道府県項目に都道府県・町村の値を結合してセット、市区郡項目に市区郡の値をセットする
      if (cityId == null) return {};
      return {
        [zipCodePropertyId]: zipCodeInputValue,
        [prefectureId]: getPrefectureInputItem({
          values,
          addressId: prefectureId,
          properties,
          address: {
            textSingleLine: `${prefecture}${town}`,
            singleSelectionPulldown: prefecture,
          },
        }),
        [cityId]: getAddressTextSingleLineInputItem(values, city, cityId),
      };
    case ADDRESS_UPDATE_PATTERN.allSeparate:
      // 都道府県項目と市区郡項目と町村項目にそれぞれの値をセットするパターン
      if (cityId == null || townId == null) return {};
      return {
        [zipCodePropertyId]: zipCodeInputValue,
        [prefectureId]: getPrefectureInputItem({
          values,
          addressId: prefectureId,
          properties,
          address: {
            textSingleLine: prefecture,
            singleSelectionPulldown: prefecture,
          },
        }),
        [cityId]: getAddressTextSingleLineInputItem(values, city, cityId),
        [townId]: getAddressTextSingleLineInputItem(values, town, townId),
      };
    case ADDRESS_UPDATE_PATTERN.doNothing:
    default:
      return {};
  }
};

export const autoCompleteAddress = ({
  zipCodePropertyId,
  addressData,
  formikProps,
  searchSetting,
  formPage,
  assortedDrillDownPropertiesGroup,
}: AutoCompleteAddressPayload): void => {
  const { values, setValues } = formikProps;
  if (!formPage?.properties) {
    return;
  }

  const updatedValues = getUpdatedValues({
    zipCodePropertyId,
    addressData,
    values,
    searchSetting,
    properties: formPage.properties as FormPageProperties,
  });

  const filteredUpdatedValues: FormPageInputData = Object.entries(
    updatedValues,
  ).reduce((acc, [propertyId, inputItem]) => {
    if (inputItem) {
      return {
        ...acc,
        [propertyId]: inputItem,
      };
    }
    return acc;
  }, {} as FormPageInputData);

  if (!Object.keys(filteredUpdatedValues).length) {
    return;
  }
  setValues((values) => {
    const newValues = {
      ...values,
      ...filteredUpdatedValues,
    };
    if (!assortedDrillDownPropertiesGroup?.length) {
      return newValues;
    }
    return convertValuesForDrillDownFromAssortedPropertiesGroup(
      assortedDrillDownPropertiesGroup,
      newValues,
    );
  }, true);
};
